import React, { useState, useEffect } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { useDropzone } from "react-dropzone";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import clsx from "clsx";
import { getBackendUrl } from "../../config";

import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(15, 23, 42, 0.56)",
      backdropFilter: "blur(4px)",
    },
    "& .MuiDialog-paper": {
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: "0 26px 80px rgba(15, 23, 42, 0.34)",
      border: "1px solid rgba(148, 163, 184, 0.2)",
    },
  },
  confirmationRoot: {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(15, 23, 42, 0.58)",
      backdropFilter: "blur(3px)",
    },
    "& .MuiDialog-paper": {
      borderRadius: 18,
      maxWidth: 460,
      boxShadow: "0 24px 80px rgba(15, 23, 42, 0.32)",
      overflow: "visible",
    },
  },
  dialogTitle: {
    position: "relative",
    background:
      "linear-gradient(135deg, rgba(248,250,252,0.95), rgba(241,245,249,0.92))",
    borderBottom: `1px solid rgba(148, 163, 184, 0.22)`,
    padding: theme.spacing(1.7, 2.2),
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.05rem",
    },
  },
  dialogContent: {
    padding: theme.spacing(2.4),
    minHeight: 90,
    backgroundColor: theme.palette.background.paper,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  dialogActions: {
    padding: theme.spacing(1.35, 2.2, 1.8),
    background:
      "linear-gradient(180deg, rgba(248,250,252,0.7), rgba(241,245,249,0.9))",
    borderTop: `1px solid rgba(148, 163, 184, 0.22)`,
    justifyContent: "flex-end",
  },
  actionButton: {
    borderRadius: 12,
    padding: theme.spacing(0.85, 2.1),
    boxShadow: "none",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    minWidth: 100,
  },
  cancelButton: {
    marginRight: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  secondaryButton: {
    marginRight: theme.spacing(1),
  },
  icon: {
    marginRight: theme.spacing(1),
    fontSize: "1.25rem",
    color: theme.palette.primary.main,
    background: "rgba(37, 99, 235, 0.12)",
    borderRadius: 10,
    padding: 6,
  },
  title: {
    fontSize: "1.02rem",
    display: "flex",
    alignItems: "center",
    fontWeight: 700,
  },
  confirmationTitle: {
    padding: theme.spacing(4, 4, 1),
    background: "transparent",
    borderBottom: "none",
    textAlign: "center",
    "& h2": {
      display: "block",
      fontSize: "1.7rem",
      fontWeight: 700,
      lineHeight: 1.2,
      textAlign: "center",
    },
  },
  confirmationTitleText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
  },
  confirmationIconWrap: {
    width: 74,
    height: 74,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#f59e0b",
    border: "4px solid rgba(245, 158, 11, 0.33)",
    background: "rgba(245, 158, 11, 0.1)",
    "& svg": {
      fontSize: "2.25rem",
    },
  },
  confirmationContent: {
    padding: theme.spacing(0, 4, 3),
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
    overflowY: "auto",
    textAlign: "center",
    ...theme.scrollbarStyles,
  },
  confirmationActions: {
    padding: theme.spacing(0, 4, 4),
    background: "transparent",
    borderTop: "none",
    justifyContent: "center",
    gap: theme.spacing(1.5),
  },
  confirmationActionButton: {
    borderRadius: 10,
    padding: theme.spacing(1.1, 2.6),
    boxShadow: "none",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.95rem",
    minWidth: 110,
  },
  confirmationCancelButton: {
    color: "#fff",
    border: "1px solid transparent",
    backgroundColor: "#64748b",
    "&:hover": {
      borderColor: "transparent",
      backgroundColor: "#475569",
    },
  },
  confirmationPrimaryButton: {
    backgroundColor: "#2563eb",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1d4ed8",
    },
  },
  dropzone: {
    border: "1px dashed #ccc",
    padding: theme.spacing(1),
    textAlign: "center",
    cursor: "pointer",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  imageContainer: {
    position: "relative",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  previewImage: {
    width: "100%",
    maxHeight: 120,
    objectFit: "contain",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ccc",
  },
  deleteButton: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderRadius: "50%",
    padding: 4,
    minWidth: "unset",
    width: 30,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
    },
  },
  dropzoneText: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
}));

const StandardModal = ({
  title,
  children,
  open,
  onClose,
  onConfirm,
  maxWidth = "xs",
  titleIcon,

  primaryButton = {
    text: i18n.t("confirmationModal.buttons.confirm"),
    color: "primary",
    onClick: null,
    visible: true,
    disabled: false,
    loading: false,
    startIcon: null,
  },
  secondaryButton = {
    text: i18n.t("confirmationModal.buttons.cancel"),
    color: "secondary",
    onClick: null,
    visible: true,
    disabled: false,
    startIcon: null,
  },
  tertiaryButton = {
    text: "",
    color: "",
    onClick: null,
    visible: false,
    disabled: false,
    startIcon: null,
    align: "right",
    className: "",
  },

  confirmationText,
  cancelText,
  confirmButtonColor = "primary",
  showFooter = true,
  loading = false,
  disableConfirmButton = false,
  disableCancelButton = false,
  useButtonSpinner = false,
  customOnClose,
  closeOnConfirm = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  variant = "default",
  paperStyle,

  enableDropzone = false,
  acceptedFiles = { "image/*": [] },
  maxFiles = 1,
  dropzoneText = "Arraste e solte um arquivo aqui, ou clique para selecionar",
  initialAttachment = null,
  onDropAccepted = null,
  onDeleteAttachment = null,
}) => {
  const classes = useStyles();
  const isConfirmationVariant = variant === "confirmation";
  const [attachment, setAttachment] = useState(initialAttachment);

  useEffect(() => {
    setAttachment(initialAttachment);
  }, [initialAttachment]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFiles,
    multiple: maxFiles > 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        setAttachment(maxFiles === 1 ? acceptedFiles[0] : acceptedFiles);
        if (onDropAccepted) {
          onDropAccepted(maxFiles === 1 ? acceptedFiles[0] : acceptedFiles);
        }
      }
    },
  });

  if (confirmationText) primaryButton.text = confirmationText;
  if (cancelText) secondaryButton.text = cancelText;
  if (confirmButtonColor) primaryButton.color = confirmButtonColor;
  if (loading) {
    primaryButton.loading = loading;
    primaryButton.disabled = disableConfirmButton || loading;
    secondaryButton.disabled = disableCancelButton || loading;
  } else {
    primaryButton.disabled = disableConfirmButton;
    secondaryButton.disabled = disableCancelButton;
  }

  const handleClose = () => {
    if (customOnClose) {
      customOnClose();
    } else {
      onClose(false);
    }
  };

  const handleConfirm = () => {
    if (primaryButton.onClick) {
      primaryButton.onClick(attachment);
    } else if (onConfirm) {
      onConfirm(attachment);
      if (closeOnConfirm) {
        onClose(false);
      }
    } else if (closeOnConfirm) {
      onClose(false);
    }
  };

  const handleSecondaryClick = () => {
    if (secondaryButton.onClick) {
      secondaryButton.onClick();
    } else {
      handleClose();
    }
  };

  const handleDeleteAttachment = () => {
    setAttachment(null);
    if (onDeleteAttachment) {
      onDeleteAttachment();
    }
  };

  const ConfirmButton = useButtonSpinner ? ButtonWithSpinner : Button;
  const loadingProp = primaryButton.loading
    ? { loading: primaryButton.loading.toString() }
    : {};

  const getImageUrl = (file) => {
    if (!file) return null;
    if (typeof file === "string") {
      if (file.startsWith("http://") || file.startsWith("https://")) {
        return file;
      }
      return `${getBackendUrl()}/public/${file}`;
    }
    return URL.createObjectURL(file);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (disableBackdropClick && reason === "backdropClick") return;
        if (disableEscapeKeyDown && reason === "escapeKeyDown") return;
        handleClose();
      }}
      className={clsx(classes.root, {
        [classes.confirmationRoot]: isConfirmationVariant,
      })}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        style: {
          borderRadius: isConfirmationVariant ? 18 : 12,
          ...paperStyle,
        },
      }}
    >
      <DialogTitle
        className={clsx(classes.dialogTitle, {
          [classes.confirmationTitle]: isConfirmationVariant,
        })}
      >
        <Typography
          className={clsx(classes.title, {
            [classes.confirmationTitleText]: isConfirmationVariant,
          })}
          variant="subtitle1"
          component="span"
        >
          {titleIcon &&
            (isConfirmationVariant ? (
              <span className={classes.confirmationIconWrap}>
                {React.cloneElement(titleIcon)}
              </span>
            ) : (
              React.cloneElement(titleIcon, { className: classes.icon })
            ))}
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent
        className={clsx(classes.dialogContent, {
          [classes.confirmationContent]: isConfirmationVariant,
        })}
      >
        {children}

        {enableDropzone && (
          <div>
            {!attachment ? (
              <div {...getRootProps()} className={classes.dropzone}>
                <input {...getInputProps()} />
                <p className={classes.dropzoneText}>{dropzoneText}</p>
              </div>
            ) : (
              <div className={classes.imageContainer}>
                {(typeof attachment === "object" &&
                  attachment.type?.startsWith("image/")) ||
                typeof attachment === "string" ? (
                  <img
                    src={getImageUrl(attachment)}
                    alt="Preview"
                    className={classes.previewImage}
                    onError={(e) => {
                      e.target.onerror = null;
                    }}
                  />
                ) : (
                  <div className={classes.fileInfo}>
                    {typeof attachment === "object"
                      ? attachment.name
                      : "Arquivo anexado"}
                  </div>
                )}
                <Button
                  size="small"
                  className={classes.deleteButton}
                  onClick={handleDeleteAttachment}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      {showFooter && (
        <DialogActions
          className={clsx(classes.dialogActions, {
            [classes.confirmationActions]: isConfirmationVariant,
          })}
        >
          {tertiaryButton.visible && tertiaryButton.align === "left" && (
            <div style={{ marginRight: "auto" }}>
              <Button
                variant="outlined"
                onClick={tertiaryButton.onClick}
                color={tertiaryButton.color}
                className={`${classes.actionButton} ${
                  classes.secondaryButton
                } ${tertiaryButton.className || ""}`}
                disabled={tertiaryButton.disabled}
                startIcon={tertiaryButton.startIcon}
              >
                {tertiaryButton.text}
              </Button>
            </div>
          )}

          {secondaryButton.visible !== false && (
            <Button
              variant={isConfirmationVariant ? "contained" : "outlined"}
              onClick={handleSecondaryClick}
              color={secondaryButton.color || "default"}
              className={clsx(classes.actionButton, classes.cancelButton, {
                [classes.confirmationActionButton]: isConfirmationVariant,
                [classes.confirmationCancelButton]: isConfirmationVariant,
              })}
              disabled={secondaryButton.disabled}
              startIcon={secondaryButton.startIcon}
            >
              {secondaryButton.text}
            </Button>
          )}

          {tertiaryButton.visible && tertiaryButton.align !== "left" && (
            <Button
              variant="outlined"
              onClick={tertiaryButton.onClick}
              color={tertiaryButton.color}
              className={`${classes.actionButton} ${classes.secondaryButton} ${
                tertiaryButton.className || ""
              }`}
              disabled={tertiaryButton.disabled}
              startIcon={tertiaryButton.startIcon}
            >
              {tertiaryButton.text}
            </Button>
          )}

          {primaryButton.visible !== false && (
            <ConfirmButton
              variant="contained"
              onClick={handleConfirm}
              color={primaryButton.color || "primary"}
              className={clsx(classes.actionButton, {
                [classes.confirmationActionButton]: isConfirmationVariant,
                [classes.confirmationPrimaryButton]: isConfirmationVariant,
              })}
              disabled={primaryButton.disabled}
              startIcon={primaryButton.startIcon}
              {...loadingProp}
            >
              {primaryButton.text}
            </ConfirmButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StandardModal;
